<template>
  <div ref="system_availability">
    <div class="is-flex is-align-items-flex-start mb-2">
      <div class="is-inline-block is-clickable" @click="toggleHidden()">
        <button class="arrow mr-2" :class="{ 'arrow-hidden': isHidden }">
          <i class="fas fa-chevron-down"></i>
        </button>
        <span class="mt-1 is-size-4 is-size-6-touch">System Availability</span>
      </div>
    </div>
    <div :class="{ hidden: isHidden }" class="data">
      <div class="is-flex is-justify-content-space-between mb-2">
        <div class="is-flex is-flex-direction-column is-align-items-flex-start">
          <div class="is-flex is-flex-direction-column tooltip">
            <Datepicker
              locale="en-US"
              v-if="dateOptionSelected == 'Year'"
              v-model="year"
              yearPicker
              :clearable="false"
            />
            <Datepicker
              locale="en-US"
              v-else-if="dateOptionSelected == 'Month'"
              v-model="month"
              monthPicker
              :clearable="false"
            />
            <Datepicker
              locale="en-US"
              v-else-if="dateOptionSelected == 'Week'"
              v-model="week"
              weekPicker
              :clearable="false"
            />
            <Datepicker
              locale="en-US"
              v-else-if="dateOptionSelected == 'Day'"
              v-model="day"
              dayPicker
              :clearable="false"
              :enableTimePicker="false"
            />
            <span class="tooltiptext">Select a specific date</span>
          </div>
        </div>
        <dashboard-buttons-controls
          :chartType="chartType"
          :dateOptionSelected="dateOptionSelected"
          @download-PDF="downloadPDF"
          @switch-chart-type="switchChartType"
          @set-chart-data-option="setChartDataOption"
          @set-selected-date-option="setSelectedDateOption"
        />
      </div>

      <div>
        <div
          class="is-loading-bar has-text-centered"
          v-bind:class="{ 'is-loading': this.isSystemAvailabilityLoading }"
        >
          <div class="lds-dual-ring"></div>
        </div>
        <dashboard-chart-bar
          v-if="chartType == 'Bar'"
          :chart-data="chartData"
          :chartOptions="chartOptions"
        />
        <dashboard-chart-line
          v-if="chartType == 'Line'"
          :chart-data="chartData"
          :chartOptions="chartOptions"
          :height="300"
        />
      </div>
      <hr />
      <div class="mb-5 has-text-centered">
        <span class="is-size-4"
          >System Availability
          {{ TotalPercentSystemAvailability.toFixed(2) }}%</span
        >
      </div>
      <div class="columns">
        <div
          v-for="(camera, index) in camerasTotal"
          :key="index"
          class="column has-text-centered"
        >
          <div class="has-text-grey-darker">Camera {{ camera }}</div>
          <div class="has-text-weight-bold">
            {{
              percentCams[index] !== undefined
                ? percentCams[index].toFixed(2)
                : "0"
            }}%
          </div>
          <div class="progress-container">
            <div
              class="progress-bar"
              :class="`is-cam${camera}`"
              :style="{ width: percentCams[index] + '%' }"
            ></div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import DashboardChartBar from "@/components/DashboardChartBar.vue";
import DashboardChartLine from "@/components/DashboardChartLine.vue";
import DashboardButtonsControls from "@/components/DashboardButtonsControls.vue";
import Datepicker from "@vuepic/vue-datepicker";
import "@vuepic/vue-datepicker/dist/main.css";
import MixinDashboardTotalAlerts from "@/mixins/MixinDashboardTotalAlerts.js";
import MixinDashboard from "@/mixins/MixinDashboard.js";
import MixinDashboardDates from "@/mixins/MixinDashboardDates.js";

export default {
  name: "DashboardSystemAvailability",
  components: {
    DashboardChartBar,
    DashboardChartLine,
    DashboardButtonsControls,
    Datepicker,
  },
  mixins: [MixinDashboardTotalAlerts, MixinDashboard, MixinDashboardDates],
  data() {
    return {
      chartType: "Bar",
      year: new Date().getFullYear(),
      month: { month: new Date().getMonth(), year: new Date().getFullYear() },
      week: [],
      day: new Date().toLocaleDateString("en-US"),
      dateOptionSelected: "Month",
      chartData: { labels: [], datasets: [] },
      chartOptions: {},
      percentCams: [],
      isSystemAvailabilityLoading: false,
      isHidden: false,
      days: [],
      TotalPercentSystemAvailability: 0,
      dashboardTooltipType: "percent",
    };
  },
  computed: {
    camerasTotal() {
      return this.$store.getters["GetCamerasTotal"];
    },
  },
  watch: {
    dateOptionSelected: {
      handler() {
        this.setChartDataOption();
      },
    },
    year: {
      handler() {
        this.getChartDataYear();
      },
    },
    month: {
      handler() {
        this.getChartDataMonth();
      },
    },
    week: {
      handler() {
        this.getChartDataOptionWeek();
      },
    },
    day: {
      handler() {
        this.getChartDataOptionDay();
      },
    },
  },
  methods: {
    createCameraArrays() {
      const cam_arr = [];

      for (let i = 1; i <= this.camerasTotal; i++) {
        cam_arr.push([]);
      }

      return cam_arr;
    },
    getMonthNames(dates) {
      const monthNamesArr = [];
      Object.values(dates).forEach((month) => {
        const month_obj = new Date(month + 1).getMonth() + 1;
        monthNamesArr.push(this.monthsNames[month_obj]);
      });
      return monthNamesArr;
    },
    formatDates(dates) {
      return dates.map((date) => {
        const [year, month, day] = date.split("-");
        const dateFormat = [month, day, year].join("/");
        return dateFormat;
      });
    },
    orderDates(a, b) {
      return new Date(a) - new Date(b);
    },
    extractDataAvaibality(dates, availability) {
      const camArr = this.createCameraArrays();

      Object.values(dates).forEach((value) => {
        for (let i = 0; i < this.camerasTotal; i++) {
          try {
            camArr[i].push(availability[`cam${i}`][value] ?? 0);
          } catch {
            camArr[i].push(0);
          }
        }
      });

      return camArr;
    },
    extractDataAvaibalityDay(availability) {
      const camArr = this.createCameraArrays();
      for (let hour in this.hours) {
        for (let i = 0; i < this.camerasTotal; i++) {
          try {
            camArr[i].push(availability[`cam${i}`][hour] ?? 0);
          } catch {
            camArr[i].push(0);
          }
        }
      }
      return camArr;
    },
    async getChartDataYear() {
      try {
        const date = this.getDateObject();
        this.isSystemAvailabilityLoading = true;
        const response = await axios.get(
          "/api/v2/analytics/get-system-availability-year",
          {
            params: date,
          }
        );
        const availability = response.data.availability;
        const dates = response.data.dates;
        dates.sort(this.orderDates);
        const formmatedDates = this.getMonthNames(dates);
        const camArr = this.extractDataAvaibality(dates, availability);

        const yearData = this.createDataObject(camArr);
        this.setChartDataForCamerasAndSystemAvailability(
          formmatedDates,
          yearData
        );
        this.setPercentTotalPerCam(yearData);
      } catch (error) {
        console.log(error);
      } finally {
        this.isSystemAvailabilityLoading = false;
      }
    },
    async getChartDataMonth() {
      try {
        this.isSystemAvailabilityLoading = true;
        const date = this.getDateObject();
        const response = await axios.get(
          "/api/v2/analytics/get-system-availability-month",
          {
            params: date,
          }
        );
        const availability = response.data.availability;
        const dates = response.data.dates;
        const formmatedDates = this.formatDates(dates);

        const camArr = this.extractDataAvaibality(dates, availability);
        const monthData = this.createDataObject(camArr);

        this.setChartDataForCamerasAndSystemAvailability(
          formmatedDates,
          monthData
        );
        this.setPercentTotalPerCam(monthData);
      } catch (error) {
        console.log(error);
      } finally {
        this.isSystemAvailabilityLoading = false;
      }
    },
    async getChartDataOptionWeek() {
      try {
        this.isSystemAvailabilityLoading = true;
        const date = this.getDateObject();
        const response = await axios.get(
          "/api/v2/analytics/get-system-availability-week",
          {
            params: date,
          }
        );

        const dates = response.data.dates;
        const availability = response.data.availability;
        const camArr = this.extractDataAvaibality(dates, availability);
        const weekData = this.createDataObject(camArr);

        this.setChartDataForCamerasAndSystemAvailability(
          this.weekDaysNames,
          weekData
        );
        this.setPercentTotalPerCam(weekData);
      } catch (error) {
        console.log(error);
      } finally {
        this.isSystemAvailabilityLoading = false;
      }
    },
    async getChartDataOptionDay() {
      try {
        this.isSystemAvailabilityLoading = true;

        const date = {
          date: new Date(this.day)
            .toLocaleDateString("pt-br")
            .replaceAll("/", "-"),
        };

        const response = await axios.get(
          "/api/v2/analytics/get-system-availability-day",
          {
            params: date,
          }
        );
        const availability = response.data.availability;
        const camArr = this.extractDataAvaibalityDay(availability);
        const dayData = this.createDataObject(camArr);

        this.setChartDataForCamerasAndSystemAvailability(this.hours, dayData);
        this.setPercentTotalPerCam(dayData);
      } catch (error) {
        console.log(error);
      } finally {
        this.isSystemAvailabilityLoading = false;
      }
    },
    configureChartDatasets() {
      this.chartData.datasets = [];
      for (let i = 1; i <= this.camerasTotal; i++) {
        const color = getComputedStyle(
          document.documentElement
        ).getPropertyValue(`--cam${i}`);
        this.chartData.datasets.push({
          label: `Camera ${i}`,
          backgroundColor: color,
          pointBackgroundColor: color,
          borderColor: color,
          borderWidth: 2,
          data: [],
          tension: 0.3,
        });
      }
    },
    setPercentTotalPerCam(data) {
      let totalPercent = 0;
      this.percentCams = [];

      for (let i = 1; i <= this.camerasTotal; i++) {
        let key = `cam${i}`;
        let totalCam = data[key].reduce(this.sum, 0);
        let _percentCam = totalCam / data[key].length;
        this.percentCams.push(isNaN(_percentCam) ? 0 : _percentCam);
        totalPercent += this.percentCams[i - 1];
      }

      this.TotalPercentSystemAvailability = totalPercent / this.camerasTotal;
    },
    downloadPDF() {
      this.isClassLoading = true;
      this.notifier("Generating PDF file...", "info");
      this.generatePDF("system_availability").then(() => {
        this.notifier("PDF is ready", "success");
        this.isClassLoading = false;
      });
    },
    getDateObject() {
      let date = { dt_init: new Date(), dt_final: new Date() };
      let dateObject;
      if (this.dateOptionSelected == "Month") {
        dateObject = new Date(this.month.year, this.month.month);
        date.dt_init = dateObject
          .toLocaleDateString("pt-br")
          .replaceAll("/", "-");
        dateObject = new Date(this.month.year, this.month.month + 1, 0);
        date.dt_final = dateObject
          .toLocaleDateString("pt-br")
          .replaceAll("/", "-");
      } else if (this.dateOptionSelected == "Day") {
        dateObject = new Date(this.day);
        date.dt_init = dateObject
          .toLocaleDateString("pt-br")
          .replaceAll("/", "-");
        date.dt_final = dateObject
          .toLocaleDateString("pt-br")
          .replaceAll("/", "-");
      } else if (this.dateOptionSelected == "Week") {
        dateObject = new Date(this.week[0]);
        date.dt_init = dateObject
          .toLocaleDateString("pt-br")
          .replaceAll("/", "-");
        dateObject = new Date(this.week[1]);
        date.dt_final = dateObject
          .toLocaleDateString("pt-br")
          .replaceAll("/", "-");
      } else if (this.dateOptionSelected == "Year") {
        dateObject = new Date(`${this.year}-01-01T12:00:00Z`);
        date.dt_init = dateObject
          .toLocaleDateString("pt-br")
          .replaceAll("/", "-");
        dateObject = new Date(`${this.year}-12-31T23:59:59Z`);
        date.dt_final = dateObject
          .toLocaleDateString("pt-br")
          .replaceAll("/", "-");
      }
      return date;
    },
  },
  created() {
    this.setChartDataOption();
    this.configureChartDatasets();
  },
};
</script>
